import type { VisualEditorPage, VisualEditorWidget } from '../types.ts';
import { liveDomUpdater } from '../services/liveDomUpdater.ts';
import { visualEditorApi } from '../services/visualEditorApi.ts';
import {
  type DrawerTab,
  type VisualEditorDrawerCallbacks,
  CONTAINER_CHILD_TYPES,
  isLegacyFlatRepeatingField,
  showDeleteConfirmDialog,
  showFileDeleteConfirmDialog,
  setupDragAndDrop,
  moveWidget,
  setupFlowItemsDragAndDrop,
  moveSubItem,
  renderActiveTabContent,
  attachDrawerEventListeners,
} from './drawer/index.ts';

export type { DrawerTab, VisualEditorDrawerCallbacks };
export { CONTAINER_CHILD_TYPES, isLegacyFlatRepeatingField };

export class VisualEditorDrawer {
  private drawerElement: HTMLElement | null = null;
  private backdropElement: HTMLElement | null = null;
  private toastElement: HTMLElement | null = null;
  private confirmDialogElement: HTMLElement | null = null;
  private activeTab: DrawerTab = 'editor';
  private page: VisualEditorPage | null = null;
  private selectedWidgetId: string | null = null;
  private editedValues: Record<string, unknown> = {};
  private callbacks: VisualEditorDrawerCallbacks | null = null;
  private justDropped: boolean = false;
  private pendingInsertion: {
    widgetId: string;
    position: 'before' | 'after';
    displayText: string;
  } | null = null;

  public render(parentElement: HTMLElement, callbacks: VisualEditorDrawerCallbacks): void {
    this.callbacks = callbacks;

    this.backdropElement = document.createElement('div');
    this.backdropElement.className = 've-drawer-backdrop';
    this.backdropElement.id = 've-drawer-backdrop';
    this.backdropElement.addEventListener('click', () => this.close());
    parentElement.appendChild(this.backdropElement);

    this.drawerElement = document.createElement('aside');
    this.drawerElement.className = 've-drawer loumars-visual-editor-drawer';
    this.drawerElement.id = 've-drawer';
    this.drawerElement.setAttribute('data-id', 'loumars-visual-editor-drawer');
    this.drawerElement.setAttribute('role', 'dialog');
    this.drawerElement.setAttribute('aria-label', 'Visual Content Editor');
    parentElement.appendChild(this.drawerElement);

    this.toastElement = document.createElement('div');
    this.toastElement.className = 've-status-toast loumars-visual-toast';
    this.toastElement.id = 've-toast';
    parentElement.appendChild(this.toastElement);

    this.confirmDialogElement = document.createElement('div');
    this.confirmDialogElement.className = 've-modal-backdrop';
    this.confirmDialogElement.id = 've-confirm-modal';
    this.confirmDialogElement.setAttribute('role', 'dialog');
    this.confirmDialogElement.setAttribute('aria-modal', 'true');
    this.confirmDialogElement.setAttribute('aria-hidden', 'true');
    parentElement.appendChild(this.confirmDialogElement);
  }

  public open(page: VisualEditorPage, focusWidgetId?: string, tab: DrawerTab = 'editor'): void {
    this.page = page;

    if (focusWidgetId && focusWidgetId.trim() !== '') {
      if (focusWidgetId === page.contentItemId) {
        this.selectedWidgetId = page.contentItemId;
        this.activeTab = 'settings';
      } else {
        const match =
          this.findWidgetRecursive(focusWidgetId) ||
          page.widgets.find((w) => w.contentType.toLowerCase() === focusWidgetId.toLowerCase());
        if (match) {
          this.selectedWidgetId = match.contentItemId;
          this.activeTab = tab;
        } else {
          this.selectedWidgetId = null;
          this.activeTab = tab === 'editor' ? 'tree' : tab;
        }
      }
    } else if (tab === 'add' || tab === 'settings' || tab === 'tree') {
      this.selectedWidgetId = null;
      this.activeTab = tab;
    } else if (page.widgets.length > 0) {
      this.selectedWidgetId = page.widgets[0].contentItemId;
      this.activeTab = tab;
    } else {
      this.selectedWidgetId = page.contentItemId;
      this.activeTab = tab === 'editor' ? 'settings' : tab;
    }

    this.initEditedValues();
    this.renderContent();

    this.backdropElement?.classList.add('open');
    this.drawerElement?.classList.add('open');
    this.callbacks?.onOpen?.();
  }

  public openWithInsertionTarget(
    targetWidgetId: string,
    position: 'before' | 'after',
    targetDisplayText?: string
  ): void {
    if (!this.page) return;
    const target = this.findWidgetRecursive(targetWidgetId);
    const displayText = targetDisplayText || target?.displayText || target?.contentType || 'Widget';
    this.pendingInsertion = {
      widgetId: targetWidgetId,
      position,
      displayText,
    };
    this.open(this.page, undefined, 'add');
  }

  public getPendingInsertion(): {
    widgetId: string;
    position: 'before' | 'after';
    displayText: string;
  } | null {
    return this.pendingInsertion;
  }

  public close(): void {
    this.backdropElement?.classList.remove('open');
    this.drawerElement?.classList.remove('open');
    this.callbacks?.onClose();
  }

  public setTab(tab: DrawerTab): void {
    this.activeTab = tab;
    this.renderContent();
  }

  public getActiveTab(): DrawerTab {
    return this.activeTab;
  }

  public getDrawerElement(): HTMLElement | null {
    return this.drawerElement;
  }

  public showToast(message: string): void {
    if (!this.toastElement) return;
    this.toastElement.textContent = message;
    this.toastElement.classList.add('visible', 'show');
    setTimeout(() => {
      this.toastElement?.classList.remove('visible', 'show');
    }, 2500);
  }

  public promptDeleteConfirm(
    widgetId: string,
    widgetName: string,
    onConfirm: () => Promise<void> | void
  ): void {
    showDeleteConfirmDialog(this.confirmDialogElement, widgetId, widgetName, onConfirm);
  }

  public promptFileDeleteConfirm(
    fileName: string,
    fileUrl: string,
    onConfirm: () => Promise<void> | void
  ): void {
    showFileDeleteConfirmDialog(this.confirmDialogElement, fileName, fileUrl, onConfirm);
  }

  public getConfirmDialogElement(): HTMLElement | null {
    return this.confirmDialogElement;
  }

  public destroy(): void {
    this.backdropElement?.remove();
    this.drawerElement?.remove();
    this.toastElement?.remove();
    this.confirmDialogElement?.remove();
    this.backdropElement = null;
    this.drawerElement = null;
    this.toastElement = null;
    this.confirmDialogElement = null;
  }

  public findWidgetRecursive(id: string | null): VisualEditorWidget | null {
    if (!this.page || !id) return null;
    const search = (list: VisualEditorWidget[]): VisualEditorWidget | null => {
      for (const w of list) {
        if (w.contentItemId === id) return w;
        if (w.subItems && w.subItems.length > 0) {
          const found = search(w.subItems);
          if (found) return found;
        }
      }
      return null;
    };
    return search(this.page.widgets);
  }

  public findParentWidget(childId: string): VisualEditorWidget | null {
    if (!this.page || !childId) return null;
    for (const w of this.page.widgets) {
      if (w.subItems && w.subItems.some((sub) => sub.contentItemId === childId)) {
        return w;
      }
    }
    return null;
  }

  public selectWidget(id: string): void {
    this.selectedWidgetId = id;
    this.activeTab = 'editor';
    this.initEditedValues();
    this.renderContent();

    const domTarget = document.querySelector(`[data-content-item-id="${id}"]`);
    domTarget?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  public getSelectedWidgetId(): string | null {
    return this.selectedWidgetId;
  }

  private initEditedValues(): void {
    this.editedValues = {};
    if (!this.page || !this.selectedWidgetId) return;

    const widget = this.findWidgetRecursive(this.selectedWidgetId);
    if (widget) {
      widget.fields.forEach((f) => {
        this.editedValues[f.key] = f.value;
      });
    }
  }

  private renderContent(): void {
    if (!this.drawerElement || !this.page) return;

    const currentWidget = this.findWidgetRecursive(this.selectedWidgetId);
    const widgetType = currentWidget?.contentType || 'Page';

    this.drawerElement.innerHTML = `
      <header class="ve-drawer-header">
        <div class="ve-header-title-box">
          <span class="ve-header-cad-tag">[ EDIT // ${widgetType.toUpperCase()} ]</span>
          <h2 class="ve-header-title">${currentWidget?.displayText || this.page.displayText || 'Visual Editor'}</h2>
        </div>
        <button type="button" class="ve-close-btn" id="ve-btn-close" aria-label="Close drawer">✕</button>
      </header>

      <nav class="ve-drawer-tabs loumars-visual-tabs" role="tablist">
        <button type="button" class="ve-tab-btn ${this.activeTab === 'editor' ? 'active' : ''}" data-tab="editor" data-alias="blocks" role="tab">Block</button>
        <button type="button" class="ve-tab-btn ${this.activeTab === 'tree' ? 'active' : ''}" data-tab="tree" role="tab">Widgets (${this.page.widgets.length})</button>
        <button type="button" class="ve-tab-btn ${this.activeTab === 'add' ? 'active' : ''}" data-tab="add" data-alias="catalog" role="tab">+ Add</button>
        <button type="button" class="ve-tab-btn ${this.activeTab === 'settings' ? 'active' : ''}" data-tab="settings" role="tab">SEO</button>
      </nav>

      <div class="ve-drawer-body loumars-visual-block-body" id="ve-drawer-body">
        ${renderActiveTabContent(this.getTabRenderersContext(), currentWidget)}
      </div>

      <footer class="ve-drawer-footer">
        <button type="button" class="ve-btn ve-btn-discard" id="ve-btn-discard" data-action="discard">Discard</button>
        <button type="button" class="ve-btn ve-btn-draft" id="ve-btn-draft" data-action="save-draft">Save Draft</button>
        <button type="button" class="ve-btn ve-btn-publish" id="ve-btn-publish" data-action="publish">Publish Changes</button>
      </footer>
    `;

    this.attachEventListeners();
  }

  private getTabRenderersContext() {
    return {
      page: this.page,
      selectedWidgetId: this.selectedWidgetId,
      activeTab: this.activeTab,
      editedValues: this.editedValues,
      pendingInsertion: this.pendingInsertion,
      findWidgetRecursive: (id: string | null) => this.findWidgetRecursive(id),
      findParentWidget: (id: string) => this.findParentWidget(id),
    };
  }

  private attachEventListeners(): void {
    attachDrawerEventListeners({
      drawerElement: this.drawerElement,
      page: this.page,
      activeTab: this.activeTab,
      setActiveTab: (tab) => {
        this.activeTab = tab;
      },
      selectedWidgetId: this.selectedWidgetId,
      editedValues: this.editedValues,
      pendingInsertion: this.pendingInsertion,
      setPendingInsertion: (val) => {
        this.pendingInsertion = val;
      },
      callbacks: this.callbacks,
      isJustDropped: () => this.justDropped,
      close: () => this.close(),
      renderContent: () => this.renderContent(),
      showToast: (msg) => this.showToast(msg),
      selectWidget: (id) => this.selectWidget(id),
      findWidgetRecursive: (id) => this.findWidgetRecursive(id),
      findParentWidget: (id) => this.findParentWidget(id),
      promptDeleteConfirm: (id, name, onConfirm) => this.promptDeleteConfirm(id, name, onConfirm),
      promptFileDeleteConfirm: (fileName, url, onConfirm) =>
        this.promptFileDeleteConfirm(fileName, url, onConfirm),
      handleFileUpload: (file, key) => this.handleFileUpload(file, key),
      handleFileRemove: (key, url, index) => this.handleFileRemove(key, url, index),
      setupDragAndDrop: () => this.setupDragAndDrop(),
      setupFlowItemsDragAndDrop: () => this.setupFlowItemsDragAndDrop(),
      moveWidget: (widgetId, dir) => this.moveWidget(widgetId, dir),
      moveSubItem: (parentId, subItemId, dir) => this.moveSubItem(parentId, subItemId, dir),
    });
  }

  private setupDragAndDrop(): void {
    setupDragAndDrop(this.getDragDropContext());
  }

  private moveWidget(widgetId: string, direction: number): void {
    moveWidget(this.getDragDropContext(), widgetId, direction);
  }

  private setupFlowItemsDragAndDrop(): void {
    setupFlowItemsDragAndDrop(this.getDragDropContext());
  }

  private moveSubItem(parentId: string, subItemId: string, direction: number): void {
    moveSubItem(this.getDragDropContext(), parentId, subItemId, direction);
  }

  private getDragDropContext() {
    return {
      drawerElement: this.drawerElement,
      page: this.page,
      callbacks: this.callbacks,
      renderContent: () => this.renderContent(),
      showToast: (msg: string) => this.showToast(msg),
      findWidgetRecursive: (id: string | null) => this.findWidgetRecursive(id),
      setJustDropped: (val: boolean) => {
        this.justDropped = val;
      },
    };
  }

  private async handleFileUpload(file: File, fieldKey: string): Promise<void> {
    this.showToast(`Uploading ${file.name}...`);
    try {
      const result = await visualEditorApi.uploadMedia(file);
      this.editedValues[fieldKey] = result.url;

      const textInput = document.getElementById(`ve-input-${fieldKey}`) as HTMLInputElement;
      if (textInput) {
        textInput.value = result.url;
      }

      if (this.selectedWidgetId) {
        liveDomUpdater.updateWidgetField(this.selectedWidgetId, fieldKey, result.url);
      }

      this.showToast(`Uploaded ${result.fileName}`);
      this.renderContent();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.showToast(`Upload failed: ${msg}`);
    }
  }

  public async handleFileRemove(fieldKey: string, fileUrl: string, index: number): Promise<void> {
    const currentWidget = this.findWidgetRecursive(this.selectedWidgetId);
    const currentVal =
      this.editedValues[fieldKey] !== undefined
        ? this.editedValues[fieldKey]
        : currentWidget?.fields.find((f) => f.key === fieldKey)?.value;

    let newVal: string | string[] = '';
    if (Array.isArray(currentVal)) {
      const arr = [...currentVal];
      if (index >= 0 && index < arr.length) {
        arr.splice(index, 1);
      } else {
        const foundIdx = arr.findIndex((item) => String(item) === fileUrl);
        if (foundIdx !== -1) arr.splice(foundIdx, 1);
      }
      newVal = arr;
    } else if (typeof currentVal === 'string' && currentVal.includes(',')) {
      const parts = currentVal
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (index >= 0 && index < parts.length) {
        parts.splice(index, 1);
      } else {
        const foundIdx = parts.findIndex((item) => item === fileUrl);
        if (foundIdx !== -1) parts.splice(foundIdx, 1);
      }
      newVal = parts.join(', ');
    } else {
      newVal = '';
    }

    this.editedValues[fieldKey] = newVal;

    const displayVal = Array.isArray(newVal) ? newVal.join(', ') : newVal;
    const textInput = document.getElementById(`ve-input-${fieldKey}`) as HTMLInputElement;
    if (textInput) {
      textInput.value = displayVal;
    }

    if (this.selectedWidgetId) {
      liveDomUpdater.updateWidgetField(this.selectedWidgetId, fieldKey, displayVal);
    }

    const fileName = fileUrl.split('/').pop() || 'file';
    this.showToast(`Removing ${fileName}...`);

    try {
      if (
        fileUrl.startsWith('/media/') ||
        fileUrl.startsWith('~/media/') ||
        fileUrl.startsWith('media/') ||
        (!fileUrl.startsWith('http://') &&
          !fileUrl.startsWith('https://') &&
          !fileUrl.startsWith('data:'))
      ) {
        await visualEditorApi.deleteMedia(fileUrl);
      }
      this.showToast(`Removed ${fileName}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[VisualEditor] Media delete warning:', msg);
      this.showToast(`Removed attachment (${msg})`);
    }

    this.renderContent();
  }
}
